import { Link } from "react-router-dom";
import EnrollmentAccessBadge from './EnrollmentAccessBadge';
import { useEffect, useMemo, useState } from "react"
import { createPortal } from "react-dom"
import { ReactComponent as BookIcon } from "../assets/images/icons/ic-book-inactive.svg"
import { ReactComponent as CheckIcon } from "../assets/images/icons/ic-check.svg"
import { ReactComponent as ClockIcon } from "../assets/images/icons/ic-clock.svg"
import { ReactComponent as LessonIcon } from "../assets/images/icons/ic-lessons-active.svg"
import { ReactComponent as ExportIcon } from "../assets/images/icons/ic-export.svg"
import { formatDateV2, formatSeconds, formatTimeV2 } from "../utils"
import Button from "./Button"
import { DialogStatus } from "../enums"
import AlertDialog from "./dialogs/AlertDialog"
import Dialog from "./dialogs/Dialog"
import styles from "./StudentProgress.module.css"
import Table from "./Table"

const safePercentage = (value, total) => {
    const duration = Number(total)
    const watched = Number(value)
    return duration > 0 && Number.isFinite(duration) && Number.isFinite(watched)
        ? Math.max(0, Math.min(100, Math.round(watched * 100 / duration))) : 0
}

const ProgressCell = ({ value, threshold }) => <div className={styles.cellProgress}>
    <span>{value}%</span><div className={styles.cellTrack} aria-hidden="true"><span style={{ width: `${value}%`, background: value >= threshold ? 'var(--primary)' : undefined }} /></div>
</div>

export const getAttendanceSessions = (lesson) => {
    const start = new Date(lesson.starts_at).getTime()
    const duration = Number(lesson.duration)
    if (!Number.isFinite(start) || !Number.isFinite(duration) || duration <= 0) return []
    const end = start + duration * 1000
    return (lesson.sessions || []).map(session => {
        const joined = Math.max(start, new Date(session.joined_at).getTime())
        const ended = Math.min(end, new Date(session.ended_at).getTime())
        if (!Number.isFinite(joined) || !Number.isFinite(ended) || ended <= joined) return null
        return { joined_at: new Date(joined).toISOString(), ended_at: new Date(ended).toISOString(),
            duration: (ended - joined) / 1000, left: (joined - start) / (end - start) * 100,
            width: (ended - joined) / (end - start) * 100 }
    }).filter(Boolean).sort((a, b) => a.left - b.left)
}

const StudentProgress = ({ student, onSetLessonCompleted = async () => null, canSetLessonCompleted = true, modern = false, profileHref, editionName }) => {

    const [innerStudent, setInnerStudent] = useState(null)
    const [lessonDetail, setLessonDetail] = useState(null)

    const viewedLessons = useMemo(() => {
        if (!innerStudent) {
            return 0
        }
        if (!innerStudent.modules || !Array.isArray(innerStudent.modules)) {
            return 0
        }

        return innerStudent.modules.reduce((total, module) => {
            if (module.lessons && Array.isArray(module.lessons)) {
                const validLessons = module.lessons.filter(
                    lesson => lesson.mandatory !== false && lesson.completed
                );
                return total + validLessons.length;
            }
            return total;
        }, 0);
    }, [innerStudent])

    const totalLessons = useMemo(() => {
        if (!innerStudent) {
            return 0
        }
         if (!innerStudent.modules || !Array.isArray(innerStudent.modules)) {
            return 0
        }

        return innerStudent.modules.reduce((total, module) => {
            if (module.lessons && Array.isArray(module.lessons)) {
                return total + module.lessons.filter(lesson => lesson.mandatory !== false).length;
            }
            return total;
        }, 0);
    }, [innerStudent])

    const modulesCount = useMemo(() => {
        if (!innerStudent?.modules || !Array.isArray(innerStudent.modules)) {
            return 0
        }
        return innerStudent.modules.length
    }, [innerStudent])

    const hasMultipleModules = modulesCount > 1

    const progressColumns = useMemo(() => {
        const onDemandColumns = [
            { key: 'position', label: 'N.', type: 'number' },
            { key: 'name', label: 'LEZIONE', type: 'string' },
            { key: 'completed', label: 'STATO', type: 'custom' },
            { key: 'recorded', label: 'AVANZAMENTO', type: 'custom' },
            { key: 'viewed_at', label: 'VISTA IL', type: 'datetime' },
        ]

        const liveColumns = [
            { key: 'name', label: 'LEZIONE', type: 'string' },
            { key: 'date', label: 'DATA', type: 'date' },
            { key: 'hour', label: 'ORARIO', type: 'string' },
            { key: 'completed', label: 'STATO', type: 'custom' },
            { key: 'live', label: 'LIVE', type: 'custom' },
            { key: 'recorded', label: 'REGISTRAZIONI', type: 'custom' },
        ]

        if (hasMultipleModules) {
            onDemandColumns.splice(4, 0, { key: 'module', label: 'MODULO', type: 'custom' })
            liveColumns.push({ key: 'module', label: 'MODULO', type: 'custom' })
        }

        return innerStudent?.edition_type === 'on_demand' ? onDemandColumns : liveColumns
    }, [innerStudent?.edition_type, hasMultipleModules])

    const formatHours = (seconds) => {
        const safeSeconds = Math.max(0, Math.floor(Number(seconds) || 0))
        const totalMinutes = Math.floor(safeSeconds / 60)
        const hours = Math.floor(totalMinutes / 60)
        const minutes = totalMinutes % 60

        if (hours <= 0) {
            return `${minutes}m`
        }

        return `${hours}h ${minutes}m`
    }

    useEffect(() => {
        let studentClone = { ...student }
        studentClone.modules = (studentClone.modules || []).map(m => ({ ...m, collapsed: true }))
        setInnerStudent(studentClone)
    }, [student])

    const percentage = useMemo(() => {
        if (!innerStudent) {
            return 0
        }
        if (innerStudent.edition_type === 'live') {
            const duration = Number(innerStudent.edition_duration) || 0
            const progress = Number(innerStudent.edition_seconds_progress) || 0
            if (duration <= 0) {
                return 0
            }
            const p = Math.round((progress * 100) / duration)
            if (isNaN(p)) {
                return 0
            }
            if (p < 0) {
                return 0
            }
            if (p > 100) {
                return 100
            }
            return p
        }

        if (totalLessons <= 0) {
            return 0
        }
        const p = Math.round((viewedLessons / totalLessons) * 100)
        return isNaN(p) ? 0 : p
    }, [innerStudent, viewedLessons, totalLessons])

    const getLessonLivePercentage = (lesson) => {
        return safePercentage(lesson.live_duration, lesson.duration)
    }

    const getLessonRecordedPercentage = (lesson) => {
        return safePercentage(lesson.recorded_duration, lesson.video_duration)
    }

    const lessons = useMemo(() => {
        if (!innerStudent) {
            return []
        }
        return innerStudent.modules.flatMap(m => m.lessons).map(lesson => {
            const module = innerStudent.modules.find(m => m.lessons.map(l => l.id).includes(lesson.id))
            const isMandatory = lesson.mandatory !== false
            const isCompleted = getLessonLivePercentage(lesson) >= 80 || getLessonRecordedPercentage(lesson) >= 90
            return {
                id: lesson.id,
                position: lesson.position,
                edition_type: innerStudent.edition_type,
                mandatory: isMandatory,
                viewed_at: lesson.viewed_at,
                name: lesson.name,
                date: lesson.starts_at,
                hour: lesson.starts_at && lesson.ends_at ? `${formatTimeV2(lesson.starts_at)} - ${formatTimeV2(lesson.ends_at)}` : null,
                module: { value: module.position, type: 'number', component: <div style={{ whiteSpace: 'nowrap' }}>{module.name}</div> },
                live: { value: getLessonLivePercentage(lesson), type: 'number', component: <ProgressCell value={getLessonLivePercentage(lesson)} threshold={80} /> },
                recorded: { value: getLessonRecordedPercentage(lesson), type: 'number', component: <ProgressCell value={getLessonRecordedPercentage(lesson)} threshold={90} /> },
                completed: {
                    value: isMandatory ? isCompleted : -1,
                    type: 'number',
                    component: <span className={`${styles.lessonStatus} ${!isMandatory ? styles.optional : isCompleted ? styles.completed : styles.incomplete}`}>
                        {!isMandatory ? 'Facoltativa' : isCompleted ? 'Completata' : 'Da completare'}
                    </span>
                },
            }
        }).sort((a, b) => {
            if (innerStudent.edition_type === 'on_demand') {
                return a.position - b.position
            } else {
                return new Date(a.date) - new Date(b.date)
            }
        })
    }, [innerStudent])

    const exportStudentFrequencyCsv = () => {
        if (!innerStudent || !innerStudent.modules) {
            return
        }

        const rows = []
        rows.push([
            "student_id",
            "student_name",
            "student_surname",
            "edition_type",
            "module_name",
            "lesson_id",
            "lesson_name",
            "lesson_type",
            "lesson_mandatory",
            "lesson_date",
            "lesson_hour_range",
            "lesson_live_duration_seconds",
            "lesson_recorded_duration_seconds",
            "student_live_duration_seconds",
            "student_recorded_duration_seconds",
            "student_live_duration_percentage",
            "student_recorded_duration_percentage",
            "completed"
        ])

        const flattenLessons = innerStudent.modules.flatMap(module =>
            (module.lessons || []).map(lesson => {
                const livePercentage = getLessonLivePercentage(lesson)
                const recordedPercentage = getLessonRecordedPercentage(lesson)
                const isCompleted = livePercentage >= 80 || recordedPercentage >= 90

                return [
                    innerStudent.id,
                    innerStudent.name || "",
                    innerStudent.surname || "",
                    innerStudent.edition_type || "",
                    module.name || "",
                    lesson.id,
                    lesson.name || "",
                    lesson.type || "",
                    lesson.mandatory !== false ? "true" : "false",
                    lesson.starts_at ? formatDateV2(lesson.starts_at, { day: '2-digit', month: '2-digit', year: 'numeric' }) : "",
                    (lesson.starts_at && (lesson.ended_at || lesson.ends_at)) ? `${formatTimeV2(lesson.starts_at)} - ${formatTimeV2(lesson.ended_at ? lesson.ended_at : lesson.ends_at)}` : "",
                    lesson.duration || 0,
                    lesson.video_duration || 0,
                    lesson.live_duration || 0,
                    lesson.recorded_duration || 0,
                    livePercentage,
                    recordedPercentage,
                    isCompleted ? "true" : "false"
                ]
            })
        )

        rows.push(...flattenLessons)

        const escapeCell = (value) => {
            const stringValue = String(value ?? "")
            if (stringValue.includes('"') || stringValue.includes(',') || stringValue.includes('\n')) {
                return `"${stringValue.replace(/"/g, '""')}"`
            }
            return stringValue
        }

        const csv = rows.map(row => row.map(escapeCell).join(',')).join('\n')
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        const safeName = `${(innerStudent.surname || 'studente').toLowerCase()}_${(innerStudent.name || '').toLowerCase()}`.replace(/\s+/g, '_')
        const datePart = new Date().toISOString().slice(0, 10)
        link.setAttribute('href', url)
        link.setAttribute('download', `frequenza_${safeName}_${datePart}.csv`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
    }

    const displayDate = (value) => {
        if (!value) return '—'
        const date = new Date(value)
        return Number.isNaN(date.getTime()) ? '—' : new Intl.DateTimeFormat('it-IT', {
            day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
        }).format(date)
    }
    const fullName = [innerStudent?.name, innerStudent?.surname].filter(Boolean).join(' ') || 'Studente'
    const initials = [innerStudent?.name, innerStudent?.surname].filter(Boolean).map(value => value[0]).join('').toUpperCase() || 'S'

    return (
        <>
            {innerStudent &&
                <div className={`${styles.container} ${modern ? styles.modern : ''}`}>
                    {modern ? (
                        <section className={styles.studentCard} aria-label="Dati dello studente">
                            <div className={styles.identityRow}>
                                <div className={styles.avatar} aria-hidden="true">{initials}</div>
                                <div className={styles.identity}>
                                    <span className={styles.eyebrow}>Studente #{innerStudent.id}</span>
                                    <h2>{fullName}</h2>
                                    {editionName && <p>{editionName}</p>}
                                </div>
                                {profileHref && <Link className={styles.profileLink} to={profileHref} target="_blank" rel="noopener noreferrer">Apri profilo <span aria-hidden="true">↗</span><span className={styles.srOnly}> (nuova scheda)</span></Link>}
                            </div>
                            <dl className={styles.studentFacts}>
                                <div><dt>Email</dt><dd>{innerStudent.email ? <a href={`mailto:${innerStudent.email}`}>{innerStudent.email}</a> : '—'}</dd></div>
                                <div><dt>Telefono</dt><dd>{innerStudent.phone ? <a href={`tel:${innerStudent.phone}`}>{innerStudent.phone}</a> : '—'}</dd></div>
                                <div><dt>Registrazione</dt><dd>{displayDate(innerStudent.registered_at)}</dd></div>
                                <div><dt>Ultimo accesso</dt><dd>{innerStudent.last_access ? displayDate(innerStudent.last_access) : 'Nessun accesso'}</dd></div>
                            </dl>
                            <div className={styles.accessRow}>
                                <EnrollmentAccessBadge student={innerStudent} />
                                <span>{innerStudent.edition_type === 'on_demand' ? 'On demand' : innerStudent.edition_type === 'live' ? 'Live' : 'Percorso formativo'}</span>
                                {innerStudent.enrollment_source === 'free' && <span>Iscrizione gratuita</span>}
                            </div>
                        </section>
                    ) : <>
                        <EnrollmentAccessBadge student={innerStudent} detail />
                        {innerStudent.enrollment_source === 'free' && <p>Iscrizione gratuita</p>}
                    </>}
                    <div className={styles.student}>
                        <div className={styles.stats}>
                            <div className={styles.progressContainer}>
                                <div className={styles.progressInfo}>
                                    <div className={styles.statList}>
                                        <div className={styles.statBox}>
                                            <div className={styles.statHeading}>
                                                <LessonIcon />
                                                <span className={styles.boxTitle}>LEZIONI</span>
                                            </div>
                                            <div className={styles.boxContent}>
                                                {viewedLessons}<span className={styles.boxTotal}>/{totalLessons}</span>
                                            </div>
                                        </div>

                                        {hasMultipleModules && (
                                            <div className={styles.statBox}>
                                                <div className={styles.statHeading}>
                                                    <BookIcon />
                                                    <span className={styles.boxTitle}>MODULI</span>
                                                </div>
                                                <div className={styles.boxContent}>
                                                    {innerStudent.edition_module_progress ?? 0}<span className={styles.boxTotal}>/{modulesCount}</span>
                                                </div>
                                            </div>
                                        )}

                                        <div className={styles.statBox}>
                                            <div className={styles.statHeading}>
                                                <ClockIcon />
                                                <span className={styles.boxTitle}>ORE</span>
                                            </div>
                                            <div className={styles.boxContent}>
                                                {formatHours(innerStudent.edition_seconds_progress)}<span className={styles.boxTotal}>/{formatHours(innerStudent.edition_duration)}</span>
                                            </div>
                                        </div>

                                        <div className={`${styles.statBox} ${styles.progressStatBox}`}>
                                            <span className={styles.boxTitle}>AVANZAMENTO CORSO</span>
                                            <div className={styles.progressStatValue}>{percentage}%</div>
                                            <div className={styles.progressStatBar} role="progressbar" aria-label="Avanzamento corso" aria-valuenow={percentage} aria-valuemin={0} aria-valuemax={100}>
                                                <div className={styles.progressBar}>
                                                    <div className={styles.progressFill} style={{ width: `${percentage}%` }} />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className={`${styles.details} ${styles.open} ${styles.lessonTable}`}>
                        <div className={styles.detailsHeader}>
                            <div className={styles.sectionHeading}><h3>Dettaglio lezioni</h3><p>Seleziona una lezione per consultare presenze e registrazioni.</p></div>
                            <Button appearance='subtle' onClick={exportStudentFrequencyCsv}><ExportIcon style={{width:11, height:11}}/> Esporta CSV</Button>
                        </div>
                        {lessons.length === 0 ? <div className={styles.emptyState}>Non ci sono lezioni disponibili per questo studente.</div> : <Table
                            appearance="minimal"
                            columns={progressColumns}
                            elements={lessons}
                            defaultSort={innerStudent.edition_type === 'on_demand' ? { key: 'position', direction: 'asc' } : { key: 'date', direction: 'asc' }}
                            onRowClick={(lesson) => {
                                const l = innerStudent.modules.flatMap(m => m.lessons).find(les => les.id === lesson.id)
                                setLessonDetail(l)
                            }}
                        />}
                    </div>
                    {lessonDetail &&
                        <LessonDetailDialog
                            lessonDetail={lessonDetail}
                            studentName={fullName}
                            studentId={innerStudent.id}
                            canSetLessonCompleted={canSetLessonCompleted}
                            onClose={() => setLessonDetail(null)}
                            onSetCompleted={async (mode) => {
                                const updatedStudent = await onSetLessonCompleted({
                                    userId: innerStudent.id,
                                    lessonId: lessonDetail.id,
                                    mode
                                })

                                if (updatedStudent) {
                                    const selectedLesson = updatedStudent.modules
                                        .flatMap(m => m.lessons)
                                        .find(l => l.id === lessonDetail.id)

                                    setInnerStudent(updatedStudent)
                                    setLessonDetail(selectedLesson ?? null)
                                }
                            }}
                        />
                    }
                </div>
            }
        </>
    )

}

const LessonDetailDialog = ({ lessonDetail, studentName, studentId, onClose, onSetCompleted, canSetLessonCompleted = true }) => {

    const [alert, setAlert] = useState({ open: false, title: '', text: '', actions: [], status: DialogStatus.Default })

    const openCompletionConfirm = (mode) => {
        if (!canSetLessonCompleted) {
            return
        }
        const modeLabel = mode === 'live' ? 'LIVE' : 'RECORDED'
        setAlert({
            open: true,
            title: 'Conferma completamento',
            text: `Confermi di segnare come completata la lezione ${lessonDetail.name} in modalità ${modeLabel} per lo studente #${studentId}?`,
            status: DialogStatus.Default,
            actions: [
                {
                    label: 'ANNULLA',
                    inverse: true,
                    accentColor: '#fa6675',
                    onClick: async () => {
                        setAlert((prev) => ({ ...prev, open: false }))
                    }
                },
                {
                    label: 'CONFERMA',
                    accentColor: '#2fc6a0',
                    onClick: async () => {
                        await onSetCompleted(mode)
                        setAlert((prev) => ({ ...prev, open: false }))
                    }
                }
            ]
        })
    }

    const getLessonLivePercentage = (lesson) => {
        return safePercentage(lesson.live_duration, lesson.duration)
    }

    const getLessonRecordedPercentage = (lesson) => {
        return safePercentage(lesson.recorded_duration, lesson.video_duration)
    }

    const sessions = getAttendanceSessions(lessonDetail)
    const live = getLessonLivePercentage(lessonDetail)
    const recorded = getLessonRecordedPercentage(lessonDetail)
    const channels = [
        ...(lessonDetail.type === 'live' ? [{ mode: 'live', title: 'Partecipazione live', value: live,
            watched: lessonDetail.live_duration, total: lessonDetail.duration, threshold: 80 }] : []),
        { mode: 'recorded', title: 'Registrazioni', value: recorded,
            watched: lessonDetail.recorded_duration, total: lessonDetail.video_duration, threshold: 90 },
    ]

    return createPortal(
        <>
            <Dialog appearance="modern" title="Dettaglio della lezione" style={{ width: 'min(820px, 100%)' }} onClose={onClose} open={!!lessonDetail}>
                <div className={styles.sessionDetail}>
                    <header className={styles.sessionHeading}>
                        <span className={styles.eyebrow}>{studentName || `Studente #${studentId}`}</span>
                        <h2>{lessonDetail.name}</h2>
                        <div className={styles.sessionMeta}>
                            <span>{lessonDetail.type === 'live' ? 'Lezione live' : 'On demand'}</span>
                            {lessonDetail.mandatory === false && <span>Facoltativa</span>}
                            {lessonDetail.starts_at && <span>{formatDateV2(lessonDetail.starts_at, { day: 'numeric', month: 'short', year: 'numeric' })} · {formatTimeV2(lessonDetail.starts_at)}{lessonDetail.ends_at ? ` – ${formatTimeV2(lessonDetail.ended_at || lessonDetail.ends_at)}` : ''}</span>}
                        </div>
                    </header>
                    <div className={styles.channelGrid}>
                        {channels.map(channel => <section key={channel.mode} className={styles.channelCard}>
                            <div className={styles.channelHeading}><h3>{channel.title}</h3><span className={`${styles.lessonStatus} ${channel.value >= channel.threshold ? styles.completed : styles.incomplete}`}>{channel.value >= channel.threshold ? 'Completata' : 'Da completare'}</span></div>
                            <div className={styles.channelValue}><strong>{channel.value}%</strong><span>{formatSeconds(channel.watched || 0)} / {formatSeconds(channel.total || 0)}</span></div>
                            <div className={styles.channelTrack} role="progressbar" aria-label={channel.title} aria-valuenow={channel.value} aria-valuemin={0} aria-valuemax={100}><span style={{ width: `${channel.value}%` }} /></div>
                            <p>Soglia di completamento: {channel.threshold}%</p>
                            {channel.mode === 'live' && lessonDetail.joined_at && <p>Primo accesso: {formatTimeV2(lessonDetail.joined_at)}</p>}
                            {channel.mode === 'recorded' && <p>{lessonDetail.viewed_at ? `Ultima visualizzazione: ${formatDateV2(lessonDetail.viewed_at, { day: 'numeric', month: 'short', year: 'numeric' })} · ${formatTimeV2(lessonDetail.viewed_at)}` : 'Nessuna visualizzazione registrata'}</p>}
                            {canSetLessonCompleted && channel.value < channel.threshold && <Button appearance="subtle" onClick={() => openCompletionConfirm(channel.mode)}><CheckIcon className={styles.actionIcon} aria-hidden="true" /> Segna {channel.mode === 'live' ? 'live' : 'registrazione'} come completata</Button>}
                        </section>)}
                    </div>
                    {lessonDetail.type === 'live' && <section className={styles.attendance}>
                        <div className={styles.sectionHeading}><h3>Sessioni di presenza <span className={styles.sessionCount}>{sessions.length}</span></h3><p>Intervalli di connessione durante la lezione.</p></div>
                        {sessions.length ? <>
                            <div className={styles.attendanceTrack} aria-label="Intervalli di presenza">
                                {sessions.map((session, index) => <span key={index} style={{ left: `${session.left}%`, width: `${session.width}%` }} title={`${formatTimeV2(session.joined_at)} – ${formatTimeV2(session.ended_at)}`} />)}
                            </div>
                            <div className={styles.sessionTableScroll}><table className={styles.sessionTable}>
                                <thead><tr><th>Sessione</th><th>Ingresso</th><th>Uscita</th><th>Durata</th></tr></thead>
                                <tbody>{sessions.map((session, index) => <tr key={index}><td>{index + 1}</td><td>{formatTimeV2(session.joined_at)}</td><td>{formatTimeV2(session.ended_at)}</td><td>{formatSeconds(session.duration)}</td></tr>)}</tbody>
                            </table></div>
                        </> : <div className={styles.emptyState}>Nessuna sessione live registrata.</div>}
                    </section>}
                </div>
            </Dialog>
            <AlertDialog
                open={alert.open}
                title={alert.title}
                text={alert.text}
                actions={alert.actions}
                status={alert.status}
                onClose={() => setAlert((prev) => ({ ...prev, open: false }))}
                node = {document.body}
            />
        </>,
        document.querySelector('.container') || document.body
    )
}

export default StudentProgress
